import React from 'react';
import { createPortal } from 'react-dom';
import {
  applyScan,
  applyScanCountFill,
  applyScanResolution,
  clearScanCountLines,
  countAllScanDue,
  countsToSubmission,
  scanCountDraftStorageKey,
  scanCountPastTense,
  scanCountTotals,
  scanCountUnits,
  scanLookupFailed,
  setScanCountQty,
  toggleScanCountSerial,
  type ScanCountCounts,
  type ScanCountFill,
  type ScanCountLine,
  type ScanCountProblem,
  type ScanCountResolution,
  type ScanCountResult,
  type ScanCountSubmission,
  type ScanCountSubmitAttempt,
  type ScanCountSubmitOutcome,
  type ScanCountTotals,
  type ScanCountVerb,
} from '@/domain/warehouse';
import PickingScanInput, { type ScanFeedback } from '../../pages/event/order/picking/PickingScanInput';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import { ModalActionError } from '../../ui/ModalActionError';
import { CloseIcon } from '../../ui/CloseIcon';
import { readableErrorMessage } from '../../utils/appErrors';
import ReportProblemDialog from './ReportProblemDialog';
import ScanCountLines, { type ScanCountMode } from './ScanCountLines';
import {
  captureOpener,
  findScanField,
  holdFocusOnBackdrop,
  isStrayKey,
  isTextEntry,
  isTypingKey,
  revealLineRow,
  trapTabKey,
} from './dialogDom';
import { useScanCountDraft, type ScanCountDraftNotice } from './useScanCountDraft';
import './ScanCountDialog.css';

export type ScanCountSlip = {
  /** The button's label, e.g. "Scan packing slip". */
  label: string;
  /** The sentence before the button, when the slip doesn't fill these counts (a PO slip is applied on its own). */
  text?: React.ReactNode;
  /**
   * Starts the record's packing-slip flow (camera or upload). Resolve with the matched lines to
   * fill in the counts (nothing applies until Receive), `[]` when nothing matched, or nothing when
   * the viewer cancelled or the flow carries on elsewhere.
   */
  onStart: () => void | Promise<ScanCountFill | null | void>;
};

export type ScanCountDialogProps = {
  open: boolean;
  onClose: () => void;
  verb: ScanCountVerb;
  /** e.g. "Receiving at 024-Columbus Base Office". */
  eyebrow: string;
  /** e.g. "Receive PI02400031". */
  title: string;
  /** e.g. "Lighting for Riverside Schools Fall Gala · from 000-Cincinnati Base". */
  subtitle?: React.ReactNode;
  /** Keep the array stable between renders (useMemo); rows re-render only when their line changes. */
  lines: readonly ScanCountLine[];
  /**
   * Keeps the count per viewer while the dialog is closed, e.g. `${profileId}:${kind}:${recordId}`.
   * Stored line by line as `scanCountDraft:v1:${draftKey}:${verb}`, so one key serves the whole
   * record and a dialog for some of its lines (Receive selected) keeps the other lines' counts. A
   * line's count comes back only if nothing was received (or picked) on it since it was counted.
   */
  draftKey?: string;
  /** Looks up a code the lines do not match (asset barcodes, Portal labels). */
  resolveScan?: (code: string) => Promise<ScanCountResolution | null>;
  /**
   * Applies the changed lines. Resolve with nothing for full success (the draft is cleared and the
   * dialog closes), `{ appliedLineIds }` when only some lines applied, or `{ error }`. A rejection
   * or an error keeps the counts. `{ error, outcomeUnknown: true }` (no answer came back) keeps the
   * submission itself: the next press, "Try again", passes it back unchanged with `{ retry: true }`
   * unless the count was changed first.
   */
  onSubmit: (submission: ScanCountSubmission, attempt: ScanCountSubmitAttempt) => Promise<ScanCountSubmitOutcome>;
  /** Purchase orders and subrentals: the packing-slip hint and button. */
  slip?: ScanCountSlip;
  /** Shows "Report a problem"; a rejection keeps the report dialog open with the reason. */
  onReportProblem?: (problem: ScanCountProblem) => Promise<void>;
  /** Shown above the footer, e.g. `<R2ResetNotice />` on R2 purchase orders. */
  notice?: React.ReactNode;
  /** Footer note for the units still due after this count. */
  remainingNote?: (remaining: number) => string;
};

type Tone = 'ok' | 'warn' | 'error' | 'info';
type Feedback = { tone: Tone; message: string; detail?: string };
type RecentScan = { id: number; code: string; text: string; tone: Tone; at: number };
type SubmitError = { title: string; message: string };
type SlipStatus = { tone: Tone; text: string };
type FocusTarget = 'scanner' | 'submit' | 'slip' | 'problem';
type FocusRequest = { target: FocusTarget; id: number };

const RECENT_LIMIT = 10;
const SLIP_FALLBACK = 'The packing slip could not be read. Try again, or count by hand.';

function toneFor(result: ScanCountResult): Tone {
  if (result.changed) return 'ok';
  return result.matched ? 'warn' : 'error';
}

function recentText(result: ScanCountResult, lineName: string | undefined, past: string): string {
  switch (result.kind) {
    case 'counted':
      return lineName ?? '';
    case 'already-counted':
      return 'Already counted';
    case 'already-done':
      return `Already ${past}`;
    case 'line-full':
      return 'Line already full';
    case 'needs-serial':
      return 'Scan each unit';
    case 'lookup-failed':
      return 'Lookup failed';
    default:
      return 'No match';
  }
}

function toScanFeedback(result: ScanCountResult): ScanFeedback {
  return { success: result.changed, message: result.detail ? `${result.message} · ${result.detail}` : result.message };
}

function capitalize(text: string): string {
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
}

function formatClock(at: number): string {
  return new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatSavedAt(iso: string): string {
  const date = new Date(iso);
  if (!iso || Number.isNaN(date.getTime())) return '';
  if (date.toDateString() === new Date().toDateString()) return formatClock(date.getTime());
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "LED panel", "LED panel and Cable bundle" or "3 lines". */
function lineList(names: readonly string[]): string {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.length} lines`;
}

function draftNoticeText(notice: ScanCountDraftNotice, past: string): string {
  const when = formatSavedAt(notice.savedAt);
  const cleared = notice.lineNames.length ? lineList(notice.lineNames) : '';
  if (notice.kind === 'changed') {
    return `Your count on ${cleared || 'some lines'} was cleared: units were ${past} there since you counted.`;
  }
  if (notice.kind === 'restored') {
    const back = when ? `Your count from ${when} is back` : 'Your earlier count is back';
    return cleared ? `${back}, except on ${cleared}: units were ${past} there since.` : `${back}.`;
  }
  return `Your saved count${when ? ` from ${when}` : ''} was cleared: units were ${past} since then.`;
}

function defaultRemainingNote(totals: ScanCountTotals, past: string, keepsDraft: boolean): string {
  const kept = keepsDraft && totals.pending > 0 ? ' Your count is kept if you close.' : '';
  if (totals.remaining > 0) {
    return `${scanCountUnits(totals.remaining)} still due ${totals.remaining === 1 ? 'stays' : 'stay'} open.${kept}`;
  }
  if (totals.pending > 0) return `This covers every unit still due.${kept}`;
  return `Every unit is already ${past}.`;
}

function FeedbackMark({ tone }: { tone: Tone }) {
  if (tone === 'ok') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M20 6 9 17l-5-5" />
      </svg>
    );
  }
  return <span aria-hidden="true">{tone === 'info' ? '…' : '!'}</span>;
}

/**
 * The shared Receive/Pick dialog for transfers, purchase orders and subrentals (approved board
 * "ReceiveDialog"). Scan mode reuses the picking scanner (camera, hardware scanner or a typed code
 * plus Enter); Count mode adds steppers and serial toggles. Nothing applies until the primary
 * button; the record's adapter turns the submission into its RPC call.
 */
export default function ScanCountDialog(props: ScanCountDialogProps) {
  if (!props.open || typeof document === 'undefined') return null;
  return createPortal(<ScanCountDialogPanel {...props} />, document.body);
}

function ScanCountDialogPanel({
  onClose,
  verb,
  eyebrow,
  title,
  subtitle,
  lines,
  draftKey,
  resolveScan,
  onSubmit,
  slip,
  onReportProblem,
  notice,
  remainingNote,
}: ScanCountDialogProps) {
  const titleId = React.useId();
  const progressLabelId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const headingRef = React.useRef<HTMLHeadingElement>(null);
  const problemButtonRef = React.useRef<HTMLButtonElement>(null);
  const slipButtonRef = React.useRef<HTMLButtonElement>(null);
  const submitButtonRef = React.useRef<HTMLButtonElement>(null);
  const [opener] = React.useState(captureOpener);
  const storageKey = draftKey ? scanCountDraftStorageKey(draftKey, verb) : null;
  const { counts, countsRef, setCounts, notice: draftNotice, discard } = useScanCountDraft(lines, storageKey);
  const [mode, setMode] = React.useState<ScanCountMode>('scan');
  const [feedback, setFeedback] = React.useState<Feedback | null>(null);
  const [recent, setRecent] = React.useState<RecentScan[]>([]);
  const [recentLineId, setRecentLineId] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<SubmitError | null>(null);
  const [problemOpen, setProblemOpen] = React.useState(false);
  const [problemStatus, setProblemStatus] = React.useState('');
  const [slipBusy, setSlipBusy] = React.useState(false);
  const [slipStatus, setSlipStatus] = React.useState<SlipStatus | null>(null);
  const [focusRequest, setFocusRequest] = React.useState<FocusRequest | null>(null);
  // A submission whose outcome is unknown (no answer came back), kept so "Try again" sends it again
  // exactly as it was. Any change to the count drops it, and the next submit is a new one.
  const heldRef = React.useRef<ScanCountSubmission | null>(null);
  const [retryHeld, setRetryHeld] = React.useState(false);

  const mountedRef = React.useRef(true);
  const submittingRef = React.useRef(false);
  const slipBusyRef = React.useRef(false);
  const scanIdRef = React.useRef(0);
  const modeRef = React.useRef(mode);
  modeRef.current = mode;
  const problemOpenRef = React.useRef(problemOpen);
  problemOpenRef.current = problemOpen;
  // The latest props, for callbacks that must stay stable: the scanner's camera loop keeps the
  // first onScan it was given.
  const latest = React.useRef({ lines, verb, resolveScan, onSubmit, onClose, onReportProblem, slip });
  latest.current = { lines, verb, resolveScan, onSubmit, onClose, onReportProblem, slip };

  useModalScrollLock(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Focus moves into the dialog (the scanner when it shows) and goes back to the opener on close.
  React.useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) {
      (findScanField(dialog) ?? headingRef.current ?? dialog).focus({ preventScroll: true });
    }
    return () => {
      const active = document.activeElement;
      if (opener?.isConnected && (!active || active === document.body)) opener.focus({ preventScroll: true });
    };
  }, [opener]);

  // Moves focus once the render that needs it has committed (a control that just appeared, or one
  // that replaces a control that went away). Disabled or missing targets fall back to the title.
  const requestFocus = React.useCallback(function requestFocus(target: FocusTarget) {
    setFocusRequest(function nextRequest(previous) {
      return { target, id: (previous ? previous.id : 0) + 1 };
    });
  }, []);

  React.useEffect(() => {
    if (!focusRequest) return;
    const element =
      focusRequest.target === 'scanner'
        ? findScanField(dialogRef.current)
        : focusRequest.target === 'submit'
          ? submitButtonRef.current
          : focusRequest.target === 'slip'
            ? slipButtonRef.current
            : problemButtonRef.current;
    (element && !element.disabled ? element : headingRef.current)?.focus({ preventScroll: true });
  }, [focusRequest]);

  // When a focused control is disabled or removed, focus falls to the page and keys reach the
  // body. Escape, Tab and (in Scan mode) a hardware scanner's characters still belong here.
  React.useEffect(() => {
    function handleStrayKey(event: KeyboardEvent) {
      const dialog = dialogRef.current;
      if (!dialog || problemOpenRef.current || !isStrayKey(event)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        latest.current.onClose();
      } else if (event.key === 'Tab') {
        trapTabKey(event, dialog);
      } else if (modeRef.current === 'scan' && isTypingKey(event)) {
        findScanField(dialog)?.focus({ preventScroll: true });
      }
    }
    document.addEventListener('keydown', handleStrayKey);
    return () => document.removeEventListener('keydown', handleStrayKey);
  }, []);

  const recordResult = React.useCallback(function recordResult(result: ScanCountResult) {
    const { lines: currentLines, verb: currentVerb } = latest.current;
    const tone = toneFor(result);
    setFeedback({ tone, message: result.message, detail: result.detail });
    if (result.kind === 'empty') return;
    const line = result.lineId ? currentLines.find((candidate) => candidate.id === result.lineId) : undefined;
    scanIdRef.current += 1;
    const entry: RecentScan = {
      id: scanIdRef.current,
      code: result.code,
      text: recentText(result, line?.name, scanCountPastTense(currentVerb)),
      tone,
      at: Date.now(),
    };
    setRecent(function addRecent(previous) {
      return [entry, ...previous].slice(0, RECENT_LIMIT);
    });
    if (result.lineId) {
      setRecentLineId(result.lineId);
      revealLineRow(dialogRef.current, result.lineId);
    }
  }, []);

  // The person changed the count: a held retry no longer matches what is on screen.
  const editCounts = React.useCallback(
    function editCounts(next: ScanCountCounts) {
      if (next === countsRef.current) return;
      if (heldRef.current) {
        heldRef.current = null;
        setRetryHeld(false);
      }
      setCounts(next);
    },
    [countsRef, setCounts],
  );

  const handleScan = React.useCallback(
    async function handleScan(code: string): Promise<ScanFeedback | undefined> {
      if (submittingRef.current) return { success: false, message: 'Saving the count. Scan again in a moment.' };
      const { lines: currentLines, verb: currentVerb, resolveScan: resolve } = latest.current;
      const local = applyScan(currentLines, countsRef.current, code, currentVerb);
      if (local.result.matched || local.result.kind === 'empty' || !resolve) {
        if (local.result.changed) editCounts(local.counts);
        recordResult(local.result);
        return toScanFeedback(local.result);
      }
      const shown = local.result.code;
      setFeedback({ tone: 'info', message: `Looking up ${shown}…` });
      let resolution: ScanCountResolution | null = null;
      try {
        resolution = await resolve(shown);
      } catch (error) {
        if (!mountedRef.current) return undefined;
        const failed = scanLookupFailed(shown, readableErrorMessage(error, 'Check the connection and scan it again.'));
        recordResult(failed);
        return toScanFeedback(failed);
      }
      if (!mountedRef.current) return undefined;
      // Apply to the counts as they are now: other scans may have landed during the lookup.
      const outcome = resolution
        ? applyScanResolution(latest.current.lines, countsRef.current, shown, resolution, latest.current.verb)
        : local;
      if (outcome.result.changed) editCounts(outcome.counts);
      recordResult(outcome.result);
      return toScanFeedback(outcome.result);
    },
    [countsRef, editCounts, recordResult],
  );

  const selectScan = React.useCallback(
    function selectScan() {
      setMode('scan');
      requestFocus('scanner');
    },
    [requestFocus],
  );

  const selectCount = React.useCallback(function selectCount() {
    setMode('count');
  }, []);

  const handleQtyChange = React.useCallback(
    function handleQtyChange(lineId: string, qty: number) {
      editCounts(setScanCountQty(latest.current.lines, countsRef.current, lineId, qty));
    },
    [countsRef, editCounts],
  );

  const handleToggleSerial = React.useCallback(
    function handleToggleSerial(lineId: string, serialId: string) {
      editCounts(toggleScanCountSerial(latest.current.lines, countsRef.current, lineId, serialId));
    },
    [countsRef, editCounts],
  );

  const handleCountAll = React.useCallback(
    function handleCountAll() {
      editCounts(countAllScanDue(latest.current.lines, countsRef.current));
      // The button goes away once nothing is due; the next step is the primary action.
      requestFocus('submit');
    },
    [countsRef, editCounts, requestFocus],
  );

  const handleClose = React.useCallback(function handleClose() {
    latest.current.onClose();
  }, []);

  const handleSubmit = React.useCallback(
    async function handleSubmit() {
      if (submittingRef.current) return;
      const { lines: currentLines, verb: currentVerb, onSubmit: submit } = latest.current;
      // "Try again" sends the held submission exactly as it went the first time, whatever the lines
      // or counts show since (the lost one may have applied and moved them).
      const held = heldRef.current;
      const submission = held ?? countsToSubmission(currentLines, countsRef.current);
      if (!submission.length) return;
      const past = scanCountPastTense(currentVerb);
      submittingRef.current = true;
      setSubmitting(true);
      setSubmitError(null);
      try {
        const outcome = await submit(submission, { retry: held !== null });
        const submitted = submission.map((item) => item.lineId);
        const appliedIds = outcome && Array.isArray(outcome.appliedLineIds) ? outcome.appliedLineIds : null;
        const applied = appliedIds ? submitted.filter((id) => appliedIds.includes(id)) : null;
        const error = outcome && typeof outcome.error === 'string' && outcome.error.trim() ? outcome.error.trim() : null;
        heldRef.current = null;
        if (!error && (!applied || applied.length === submitted.length)) {
          // Everything applied: forget the draft (even if the dialog closed meanwhile) and close.
          discard();
          if (mountedRef.current) latest.current.onClose();
          return;
        }
        if (outcome && outcome.outcomeUnknown && !applied) {
          // No answer came back: keep this exact submission for "Try again" (the counts stay too).
          heldRef.current = submission;
          if (mountedRef.current) {
            setRetryHeld(true);
            setSubmitError({
              title: 'No answer came back',
              message: `The connection dropped before an answer came back, so this count may already be ${past}. Press Try again to send the same count once more; it is never ${past} twice.`,
            });
          }
          return;
        }
        if (mountedRef.current) setRetryHeld(false);
        // Keep only the lines that did not apply; the draft follows the counts.
        if (applied && applied.length) setCounts(clearScanCountLines(latest.current.lines, countsRef.current, applied));
        const failed = submitted.length - (applied ? applied.length : 0);
        if (mountedRef.current) {
          setSubmitError({
            title: applied && applied.length ? `Some lines were not ${past}` : `Nothing was ${past}`,
            message:
              error ??
              `${failed} ${failed === 1 ? 'line was' : 'lines were'} not ${past}. Their counts are kept, so you can try again.`,
          });
        }
      } catch (submitFailure) {
        heldRef.current = null;
        if (mountedRef.current) {
          setRetryHeld(false);
          setSubmitError({
            title: `Nothing was ${past}`,
            message: readableErrorMessage(submitFailure, `The count was not ${past}. It is kept, so you can try again.`, 'mutation'),
          });
        }
      } finally {
        submittingRef.current = false;
        if (mountedRef.current) setSubmitting(false);
      }
    },
    [countsRef, discard, setCounts],
  );

  const returnFocusAfterProblem = React.useCallback(
    function returnFocusAfterProblem() {
      // In Scan mode the scanner gets focus back, so the next hardware scan's Enter cannot press a button.
      requestFocus(modeRef.current === 'scan' ? 'scanner' : 'problem');
    },
    [requestFocus],
  );

  const openProblem = React.useCallback(function openProblem() {
    setProblemStatus('');
    setProblemOpen(true);
  }, []);

  const closeProblem = React.useCallback(
    function closeProblem() {
      setProblemOpen(false);
      returnFocusAfterProblem();
    },
    [returnFocusAfterProblem],
  );

  const saveProblem = React.useCallback(
    async function saveProblem(problem: ScanCountProblem) {
      const report = latest.current.onReportProblem;
      if (!report) return;
      await report(problem);
      if (!mountedRef.current) return;
      setProblemOpen(false);
      setProblemStatus('Problem noted on the record.');
      returnFocusAfterProblem();
    },
    [returnFocusAfterProblem],
  );

  const handleSlip = React.useCallback(
    async function handleSlip() {
      const currentSlip = latest.current.slip;
      if (!currentSlip || slipBusyRef.current) return;
      setSlipStatus(null);
      let started: unknown;
      try {
        started = currentSlip.onStart();
      } catch (error) {
        setSlipStatus({ tone: 'error', text: readableErrorMessage(error, SLIP_FALLBACK) });
        return;
      }
      if (!(started instanceof Promise)) return;
      slipBusyRef.current = true;
      setSlipBusy(true);
      let fill: unknown;
      try {
        fill = await started;
      } catch (error) {
        if (mountedRef.current) setSlipStatus({ tone: 'error', text: readableErrorMessage(error, SLIP_FALLBACK) });
        return;
      } finally {
        slipBusyRef.current = false;
        if (mountedRef.current) {
          setSlipBusy(false);
          // The button was disabled while the slip was read, which dropped focus. In Scan mode the
          // scanner takes it, so the next scan is not lost.
          requestFocus(modeRef.current === 'scan' ? 'scanner' : 'slip');
        }
      }
      if (!mountedRef.current || !Array.isArray(fill)) return;
      const filled = applyScanCountFill(latest.current.lines, countsRef.current, fill);
      editCounts(filled.counts);
      const matched = filled.lineIds.length;
      setSlipStatus(
        matched
          ? {
              tone: 'ok',
              text: `The packing slip matched ${matched} ${matched === 1 ? 'line' : 'lines'}. Check the counts, then press ${latest.current.verb}.`,
            }
          : { tone: 'warn', text: 'Nothing on the packing slip matched these lines.' },
      );
    },
    [countsRef, editCounts, requestFocus],
  );

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const dialog = dialogRef.current;
    // Keys from the nested report dialog (portalled elsewhere) are its own business.
    if (!dialog || !(event.target instanceof Node) || !dialog.contains(event.target)) return;
    if (event.key === 'Escape') {
      // A native select handles its own Escape.
      if (event.defaultPrevented || document.activeElement instanceof HTMLSelectElement) return;
      event.preventDefault();
      event.stopPropagation();
      handleClose();
      return;
    }
    if (event.key === 'Tab') {
      trapTabKey(event, dialog);
      return;
    }
    // In Scan mode, characters typed while a button (or the dialog itself) has focus go to the
    // scanner: focus moves before the character is inserted, so a hardware scanner's code lands
    // in the field and its closing Enter scans instead of pressing the button.
    if (modeRef.current === 'scan' && isTypingKey(event) && !isTextEntry(event.target)) {
      findScanField(dialog)?.focus({ preventScroll: true });
    }
  }

  const past = scanCountPastTense(verb);
  const total = React.useMemo(() => scanCountTotals(lines, counts), [lines, counts]);
  const keepsDraft = storageKey !== null;
  const closeLabel = keepsDraft && total.pending > 0 ? 'Close and keep this count' : 'Close';
  const note = !lines.length ? '' : remainingNote ? remainingNote(total.remaining) : defaultRemainingNote(total, past, keepsDraft);
  const submitLabel = submitting
    ? `${verb === 'Pick' ? 'Picking' : 'Receiving'}…`
    : retryHeld
      ? 'Try again'
      : total.pending
        ? `${verb} ${scanCountUnits(total.pending)}`
        : verb;
  const percent = total.expected ? Math.min(100, Math.round((total.done / total.expected) * 100)) : 0;
  const draftText = draftNotice ? draftNoticeText(draftNotice, past) : '';

  const slipBox = slip ? (
    <div className="scan-count-slip">
      <p className="scan-count-slip__text">
        {slip.text ?? (
          <>
            <strong>Receiving a purchase order or subrental?</strong> Scan or upload the packing slip. Matches fill in
            these counts, and nothing applies until you press {verb}.
          </>
        )}
      </p>
      <button
        ref={slipButtonRef}
        type="button"
        className="btn btn-secondary sm scan-count-slip__button"
        onClick={handleSlip}
        disabled={slipBusy || submitting}
      >
        {slipBusy ? 'Reading the packing slip…' : slip.label}
      </button>
      <div className="scan-count-slip__status" role="status" aria-live="polite">
        {slipStatus ? <p data-tone={slipStatus.tone}>{slipStatus.text}</p> : null}
      </div>
    </div>
  ) : null;

  return (
    <div className="modal-overlay scan-count-overlay" onMouseDown={holdFocusOnBackdrop}>
      <div
        ref={dialogRef}
        className="modal modal--xl scan-count-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-mode={mode}
        onKeyDown={handleKeyDown}
      >
        <header className="modal__header scan-count-dialog__header">
          <div className="scan-count-dialog__heading">
            {eyebrow ? <p className="scan-count-dialog__eyebrow">{eyebrow}</p> : null}
            <h2 ref={headingRef} id={titleId} className="modal__title scan-count-dialog__title" tabIndex={-1}>
              {title}
            </h2>
            {subtitle ? <p className="scan-count-dialog__subtitle">{subtitle}</p> : null}
          </div>
          <button type="button" className="btn-close" aria-label={closeLabel} title={closeLabel} onClick={handleClose}>
            <CloseIcon />
          </button>
        </header>

        <div className="modal__body">
          <div className="modal__body-content scan-count-dialog__content">
            <div className="scan-count-dialog__toolbar">
              <div className="scan-count-dialog__modes" role="group" aria-label={`${verb} by`}>
                <button type="button" className="scan-count-dialog__mode" aria-pressed={mode === 'scan'} onClick={selectScan}>
                  Scan
                </button>
                <button type="button" className="scan-count-dialog__mode" aria-pressed={mode === 'count'} onClick={selectCount}>
                  Count
                </button>
              </div>
              {draftText ? <p className="scan-count-dialog__draft-note">{draftText}</p> : null}
              {mode === 'count' && total.remaining > 0 ? (
                <button
                  type="button"
                  className="btn btn-secondary sm scan-count-dialog__count-all"
                  onClick={handleCountAll}
                  disabled={submitting}
                >
                  Count everything still due
                </button>
              ) : null}
            </div>

            {!lines.length ? (
              <p className="scan-count-dialog__empty">Nothing to {verb.toLowerCase()} on this record.</p>
            ) : (
              <div className="scan-count-dialog__layout" data-mode={mode}>
                {mode === 'scan' ? (
                  <div className="scan-count-dialog__scan-panel">
                    <div className="scan-count-dialog__scanner">
                      <PickingScanInput
                        onScan={handleScan}
                        label="Scan a serial, barcode or Portal label"
                        placeholder="Scan or type a code"
                        showManualEntry={false}
                        disabled={submitting}
                      />
                      <div className="scan-count-dialog__feedback" role="status" aria-live="polite" aria-atomic="true">
                        {feedback ? (
                          <div className="scan-count-feedback" data-tone={feedback.tone}>
                            <span className="scan-count-feedback__mark">
                              <FeedbackMark tone={feedback.tone} />
                            </span>
                            <strong className="scan-count-feedback__message">{feedback.message}</strong>
                            {feedback.detail ? <span className="scan-count-feedback__detail">{feedback.detail}</span> : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    {recent.length ? (
                      <ol className="scan-count-recent" aria-label="Recent scans">
                        {recent.map((scan) => (
                          <li key={scan.id} data-tone={scan.tone}>
                            <span className="scan-count-recent__code">{scan.code}</span>
                            <span className="scan-count-recent__text">{scan.text}</span>
                            <time className="scan-count-recent__time" dateTime={new Date(scan.at).toISOString()}>
                              {formatClock(scan.at)}
                            </time>
                          </li>
                        ))}
                      </ol>
                    ) : null}
                    {slipBox}
                  </div>
                ) : null}

                <div className="scan-count-dialog__count-panel">
                  <div className="scan-count-progress">
                    <div className="scan-count-progress__top">
                      <span id={progressLabelId}>{capitalize(past)} so far</span>
                      <strong>
                        {total.done} of {scanCountUnits(total.expected)}
                      </strong>
                    </div>
                    <div
                      className="scan-count-progress__bar"
                      role="progressbar"
                      aria-labelledby={progressLabelId}
                      aria-valuemin={0}
                      aria-valuemax={total.expected}
                      aria-valuenow={total.done}
                      aria-valuetext={`${total.done} of ${scanCountUnits(total.expected)}`}
                    >
                      <span className="scan-count-progress__fill" style={{ width: `${percent}%` }} />
                    </div>
                  </div>
                  {mode === 'count' ? slipBox : null}
                  <ScanCountLines
                    lines={lines}
                    counts={counts}
                    mode={mode}
                    verb={verb}
                    recentLineId={recentLineId}
                    disabled={submitting}
                    onQtyChange={handleQtyChange}
                    onToggleSerial={handleToggleSerial}
                  />
                  {onReportProblem ? (
                    <div className="scan-count-dialog__problem-row">
                      <p className="scan-count-dialog__problem">
                        Something damaged, missing or extra?{' '}
                        <button ref={problemButtonRef} type="button" className="scan-count-dialog__link" onClick={openProblem}>
                          Report a problem
                        </button>{' '}
                        and it is noted on the record.
                      </p>
                      <div className="scan-count-dialog__problem-status" role="status" aria-live="polite">
                        {problemStatus ? <p>{problemStatus}</p> : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>

        {notice ? <div className="scan-count-dialog__notice">{notice}</div> : null}
        <ModalActionError message={submitError ? submitError.message : null} title={submitError?.title} />
        <footer className="modal__footer scan-count-dialog__footer">
          {note ? <p className="scan-count-dialog__note">{note}</p> : null}
          <button
            ref={submitButtonRef}
            type="button"
            className="btn btn-primary scan-count-dialog__submit"
            onClick={handleSubmit}
            disabled={(!total.pending && !retryHeld) || submitting}
          >
            {submitLabel}
          </button>
        </footer>

        {problemOpen && onReportProblem ? (
          <ReportProblemDialog lines={lines} onClose={closeProblem} onSave={saveProblem} />
        ) : null}
      </div>
    </div>
  );
}
